import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-stone-400">
            <ShoppingBag className="h-12 w-12" />
            <p className="text-sm">Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="-mx-2 flex-1 space-y-3 overflow-y-auto px-2 py-4">
              {items.map((it) => (
                <div key={it.cartId} data-testid={`cart-line-${it.cartId}`} className="rounded-xl border border-stone-200 bg-white p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-stone-900">{it.name}</div>
                      {it.selected_options?.length > 0 && (
                        <div className="mt-0.5 text-xs text-stone-500">
                          {it.selected_options.map((o) => `${o.group}: ${o.label}`).join(" · ")}
                        </div>
                      )}
                      {it.special_instructions && (
                        <div className="mt-0.5 text-xs italic text-amber-700">“{it.special_instructions}”</div>
                      )}
                    </div>
                    <button
                      data-testid={`cart-remove-${it.cartId}`}
                      onClick={() => removeItem(it.cartId)}
                      className="text-stone-400 hover:text-rose-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 rounded-full border border-stone-200 p-0.5">
                      <button onClick={() => updateQty(it.cartId, it.quantity - 1)} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-stone-100">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-5 text-center text-sm font-bold">{it.quantity}</span>
                      <button onClick={() => updateQty(it.cartId, it.quantity + 1)} className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-stone-100">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <span className="font-bold text-stone-900">{money(it.unit_price * it.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-stone-200 pt-4">
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-stone-600">Subtotal</span>
                <span className="font-bold text-stone-900">{money(subtotal)}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={goCheckout}
                className="w-full rounded-full bg-orange-700 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-orange-800"
              >
                Go to Checkout · {money(subtotal)}
              </button>
              <p className="mt-2 text-center text-xs text-stone-400">Tax calculated at checkout</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
